import type { ApiCacheTag } from "../resources/types";
import type { ClientType } from "../routing/clientType";

export type ServerPagedCacheRegistration = Readonly<{
  cacheTag: ApiCacheTag;
  clientType: ClientType;
  route: string;
  collectionRoute?: string;
}>;

type ServerPagedRevalidator = () => Promise<unknown>;

type RegisteredServerPagedCache = ServerPagedCacheRegistration &
  Readonly<{
    revalidators: Set<ServerPagedRevalidator>;
  }>;

const registrations = new Map<string, RegisteredServerPagedCache>();

export function registerServerPagedCache(
  queryKey: string,
  registration: ServerPagedCacheRegistration,
  revalidate: ServerPagedRevalidator,
) {
  let registeredCache = registrations.get(queryKey);
  if (registeredCache == null) {
    registeredCache = {
      ...registration,
      revalidators: new Set(),
    };
    registrations.set(queryKey, registeredCache);
  }

  registeredCache.revalidators.add(revalidate);

  return function unregister() {
    registeredCache.revalidators.delete(revalidate);
    if (registeredCache.revalidators.size) return;

    registrations.delete(queryKey);
  };
}

export async function revalidateServerPagedCaches(
  matches: (registration: ServerPagedCacheRegistration) => boolean,
) {
  const revalidations: Promise<unknown>[] = [];
  for (const registration of registrations.values()) {
    if (!matches(registration)) continue;

    const [revalidate] = registration.revalidators;
    if (revalidate) revalidations.push(revalidate());
  }

  await Promise.all(revalidations);
}
